import type { Issue, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, ProjectDocument } from './document.js';
import type { EvaluatedProject } from './project.js';
import { cabinetWorldBox } from './project.js';
import { nextPanelKey, panelSize } from './assembly.js';
import { panelPropertyOnAxis, thicknessAxisOf } from './assemblyEdit.js';
import { propagateLinks } from './links.js';
import { mergePanels } from './panelMerge.js';
import { moveOutOfWall } from './roomMount.js';
import { evaluateProject } from './project.js';

/** Stable identity of an issue (for dismissing it): what it is about, not its wording. */
export function issueKey(i: Issue): string {
  return [i.code, i.cabinetId ?? '', i.partId ?? '', i.otherPartId ?? '', i.otherCabinetId ?? '', i.edge ?? '', i.ref ?? '', i.param ?? ''].join('|');
}

/** One way to resolve an issue; `apply` edits a draft of the document (one undo step in the editor). */
export interface IssueFix {
  id: string;
  label: string;
  title?: string;
  apply(doc: ProjectDocument): boolean;
}

const MIN_PIECE = 1000; // 1 mm: shorter leftovers are dropped
const AX = ['X', 'Y', 'Z'] as const;

const localKeyOf = (unitId: string, partId: string | undefined) => (partId?.startsWith(`${unitId}/`) ? partId.slice(unitId.length + 1) : null);

/** A plain (not repeated) panel of an assembly behind a part id. */
function panelOfPart(doc: ProjectDocument, unitId: string | undefined, partId: string | undefined): AssemblyPanel | null {
  const inst = doc.assemblies.find((a) => a.id === unitId);
  const key = unitId ? localKeyOf(unitId, partId) : null;
  const p = inst?.panels.find((x) => x.key === key);
  return p && (!p.repeat || p.repeat.count <= 1) ? p : null;
}

function extent(p: AssemblyPanel): { min: Vec3; max: Vec3 } {
  const s = panelSize(p);
  return { min: [...p.position], max: [0, 1, 2].map((k) => p.position[k]! + s[k]!) as Vec3 };
}

/** Pieces of `a` left after cutting out the slab of `b` (along b's thickness, which must lie in a's plane). */
function splitPieces(a: AssemblyPanel, b: AssemblyPanel): { axis: 0 | 1 | 2; pieces: [number, number][] } | null {
  const k = thicknessAxisOf(b);
  if (k === thicknessAxisOf(a)) return null;
  const A = extent(a);
  const B = extent(b);
  if (B.max[k] <= A.min[k] || B.min[k] >= A.max[k]) return null;
  const pieces = (
    [
      [A.min[k], Math.min(B.min[k], A.max[k])],
      [Math.max(B.max[k], A.min[k]), A.max[k]],
    ] as [number, number][]
  ).filter(([x0, x1]) => x1 - x0 >= MIN_PIECE);
  return { axis: k, pieces };
}

/**
 * Cuts panel `key` where panel `otherKey` passes through it: one panel becomes two (one on each side),
 * or is shortened when the other only reaches into one end. Returns the keys of the pieces.
 */
export function splitPanelAround(inst: AssemblyInstance, key: string, otherKey: string): string[] | null {
  const a = inst.panels.find((p) => p.key === key);
  const b = inst.panels.find((p) => p.key === otherKey);
  if (!a || !b || (a.repeat && a.repeat.count > 1)) return null;
  const s = splitPieces(a, b);
  if (!s || !s.pieces.length) return null;
  const prop = panelPropertyOnAxis(a, s.axis);
  const [first, second] = s.pieces;
  const src = structuredClone(a);
  a.position[s.axis] = first![0];
  a[prop] = first![1] - first![0];
  if (!second) return [a.key];
  const piece: AssemblyPanel = { ...src, key: nextPanelKey(inst), label: `${src.label} (dio 2)`, joints: structuredClone(src.joints) };
  piece.position[s.axis] = second[0];
  piece[prop] = second[1] - second[0];
  a.label = `${src.label} (dio 1)`;
  inst.panels.push(piece);
  // Panels that declared a joint to the original also join the new piece.
  for (const p of inst.panels) {
    if (p === piece) continue;
    for (const j of [...p.joints]) if (j.key === a.key && !p.joints.some((x) => x.key === piece.key && x.kind === j.kind)) p.joints.push({ ...j, key: piece.key });
  }
  return [a.key, piece.key];
}

/** Smallest move of `a` (along one axis) after which it only touches `b`. */
function pushOut(a: { min: Vec3; max: Vec3 }, b: { min: Vec3; max: Vec3 }, axes: (0 | 1 | 2)[]): { axis: 0 | 1 | 2; delta: number } | null {
  let best: { axis: 0 | 1 | 2; delta: number } | null = null;
  for (const k of axes) {
    for (const delta of [b.max[k] - a.min[k], b.min[k] - a.max[k]]) {
      if (!best || Math.abs(delta) < Math.abs(best.delta)) best = { axis: k, delta };
    }
  }
  return best && best.delta !== 0 ? best : null;
}

const mmText = (v: number) => `${v > 0 ? '+' : ''}${Math.round(v / 100) / 10} mm`;

function collisionFixes(doc: ProjectDocument, issue: Issue): IssueFix[] {
  const unitId = issue.cabinetId;
  const a = panelOfPart(doc, unitId, issue.partId);
  const b = panelOfPart(doc, unitId, issue.otherPartId);
  if (!unitId || !a || !b) return [];
  const fixes: IssueFix[] = [];
  const unit = (d: ProjectDocument) => d.assemblies.find((x) => x.id === unitId)!;
  for (const [x, y] of [
    [a, b],
    [b, a],
  ] as const) {
    const s = splitPieces(x, y);
    if (!s || !s.pieces.length) continue;
    fixes.push({
      id: `split:${x.key}:${y.key}`,
      label: s.pieces.length > 1 ? `Podijeli „${x.label}” na dvije oko „${y.label}”` : `Skrati „${x.label}” do „${y.label}”`,
      title: s.pieces.length > 1 ? `„${y.label}” prolazi cijela, „${x.label}” postaje dva dijela (lijevo/desno od nje).` : `„${x.label}” se skraćuje tako da se samo dodiruje s „${y.label}”.`,
      apply: (d) => !!splitPanelAround(unit(d), x.key, y.key),
    });
  }
  for (const [x, y] of [
    [a, b],
    [b, a],
  ] as const) {
    const m = pushOut(extent(x), extent(y), [0, 1, 2]);
    if (!m) continue;
    fixes.push({
      id: `move:${x.key}:${y.key}`,
      label: `Odmakni „${x.label}” (${AX[m.axis]} ${mmText(m.delta)})`,
      title: `Najmanji pomak poslije kojeg se „${x.label}” samo dodiruje s „${y.label}”.`,
      apply: (d) => {
        const p = unit(d).panels.find((q) => q.key === x.key);
        if (!p) return false;
        p.position[m.axis] += m.delta;
        return true;
      },
    });
  }
  return fixes;
}

function overlapFixes(doc: ProjectDocument, ev: EvaluatedProject, issue: Issue): IssueFix[] {
  const ca = ev.cabinets.find((c) => c.instance.id === issue.cabinetId);
  const cb = ev.cabinets.find((c) => c.instance.id === issue.otherCabinetId);
  if (!ca || !cb) return [];
  const fixes: IssueFix[] = [];
  for (const [x, y] of [
    [ca, cb],
    [cb, ca],
  ] as const) {
    if (x.instance.locked) continue;
    const m = pushOut(cabinetWorldBox(x), cabinetWorldBox(y), [0, 2]);
    if (!m) continue;
    fixes.push({
      id: `move-unit:${x.instance.id}`,
      label: `Pomjeri „${x.instance.name}” (${AX[m.axis]} ${mmText(m.delta)})`,
      title: `Najmanji pomak poslije kojeg se „${x.instance.name}” i „${y.instance.name}” samo dodiruju.`,
      apply: (d) => {
        const u = [...d.cabinets, ...d.assemblies].find((q) => q.id === x.instance.id);
        if (!u) return false;
        u.transform.position[m.axis] += m.delta;
        return true;
      },
    });
  }
  return fixes;
}

/** Moves an assembly's panels so none lies behind/below its origin; the assembly stays where it is in the room. */
export function normaliseAssemblyOrigin(inst: AssemblyInstance): boolean {
  if (inst.transform.mirrored) return false;
  const shift: Vec3 = [0, 0, 0];
  for (const p of inst.panels) {
    const e = extent(p);
    for (const k of [0, 1, 2] as const) shift[k] = Math.max(shift[k], -e.min[k]);
  }
  if (shift.every((v) => v === 0)) return false;
  return shiftAssemblyOrigin(inst, shift);
}

/**
 * Moves every panel of the assembly by `shift` (local µm) and the assembly the other way, so nothing
 * moves in the room — only the origin (where the gizmo sits) changes. Not for mirrored assemblies.
 */
export function shiftAssemblyOrigin(inst: AssemblyInstance, shift: Vec3): boolean {
  if (inst.transform.mirrored) return false;
  for (const p of inst.panels) for (const k of [0, 1, 2] as const) p.position[k] += shift[k];
  // Remembered opening rectangles of fronts are local too.
  for (const f of inst.fronts ?? [])
    for (const sp of f.spaces) {
      sp.rect.x0 += shift[0];
      sp.rect.x1 += shift[0];
      sp.rect.y0 += shift[1];
      sp.rect.y1 += shift[1];
    }
  // Same rotation as the viewer / cabinetWorldBox: x' = x·cos + z·sin, z' = −x·sin + z·cos.
  const phi = (-inst.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const t = inst.transform.position;
  t[0] -= shift[0] * cos + shift[2] * sin;
  t[1] -= shift[1];
  t[2] -= -shift[0] * sin + shift[2] * cos;
  return true;
}

/** Puts the assembly's origin at a point of the room (world µm), e.g. one of its corners; nothing moves. */
export function setAssemblyOriginAt(inst: AssemblyInstance, world: Vec3): boolean {
  const t = inst.transform.position;
  const dx = world[0] - t[0];
  const dy = world[1] - t[1];
  const dz = world[2] - t[2];
  const phi = (-inst.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  // Inverse turn: x = x'·cos − z'·sin, z = x'·sin + z'·cos.
  const local: Vec3 = [Math.round(dx * cos - dz * sin), Math.round(dy), Math.round(dx * sin + dz * cos)];
  if (local.every((v) => v === 0)) return false;
  return shiftAssemblyOrigin(inst, [-local[0], -local[1], -local[2]]);
}

function edgeFixes(doc: ProjectDocument, issue: Issue): IssueFix[] {
  const unitId = issue.cabinetId;
  const unit = (d: ProjectDocument) => [...d.cabinets, ...d.assemblies].find((u) => u.id === unitId);
  if (!unitId || !unit(doc)) return [];
  if (issue.code === 'edge.override-orphaned' && issue.ref) {
    const ref = issue.ref;
    return [{ id: 'drop-override', label: 'Ukloni zastarjelu ručnu izmjenu', apply: (d) => delete unit(d)!.edgeOverrides[ref] }];
  }
  const local = localKeyOf(unitId, issue.partId);
  if (issue.code !== 'edge.unresolved' || !local || !issue.edge) return [];
  const key = `${local}:${issue.edge}`;
  const set = (decision: 'band' | 'none', note: string) => (d: ProjectDocument) => {
    const u = unit(d);
    if (!u) return false;
    u.edgeOverrides = { ...u.edgeOverrides, [key]: { decision, note } };
    return true;
  };
  return [
    { id: 'band', label: 'Kantuj ovu ivicu', title: 'Ručna odluka: ivica dobija standardnu traku.', apply: set('band', 'Ručno: kantovano (iz provjere).') },
    { id: 'none', label: 'Bez kanta', title: 'Ručna odluka: ivica ostaje bez trake.', apply: set('none', 'Ručno: bez kanta (iz provjere).') },
  ];
}

function linkFixes(doc: ProjectDocument, issue: Issue): IssueFix[] {
  const l = (doc.links ?? []).find((x) => x.id === issue.ref);
  if (!l) return [];
  const remove: IssueFix = { id: 'unlink', label: 'Ukloni link', apply: (d) => (d.links = d.links.filter((x) => x.id !== l.id)) && true };
  if (issue.code === 'link.orphaned') return [remove];
  return [
    { id: 'sync-a', label: 'Uskladi po prvoj mjeri', title: 'Druga mjera se mijenja da odgovara prvoj (plus razlika linka).', apply: (d) => propagateLinks(d, l.a).length > 0 },
    { id: 'sync-b', label: 'Uskladi po drugoj mjeri', title: 'Prva mjera se mijenja da odgovara drugoj.', apply: (d) => propagateLinks(d, l.b).length > 0 },
    remove,
  ];
}

/** Ways to resolve an issue (empty: it can only be shown, e.g. selected for its properties). */
export function issueFixes(doc: ProjectDocument, ev: EvaluatedProject, issue: Issue): IssueFix[] {
  switch (issue.code) {
    case 'collision.static':
      return collisionFixes(doc, issue);
    case 'panel.mergeable': {
      const unitId = issue.cabinetId;
      const keep = localKeyOf(unitId ?? '', issue.partId);
      const remove = localKeyOf(unitId ?? '', issue.otherPartId);
      if (!unitId || !keep || !remove) return [];
      return [
        {
          id: 'merge',
          label: 'Spoji u jednu ploču',
          title: 'Druga ploča se briše, prva preuzima njenu dužinu; police, ladice, fronte i linkovi prelaze na nju.',
          apply: (d) => {
            const inst = d.assemblies.find((a) => a.id === unitId);
            return !!inst && !inst.locked && mergePanels(inst, keep, remove, d);
          },
        },
      ];
    }
    case 'scene.overlap':
      return overlapFixes(doc, ev, issue);
    case 'room.unit-in-wall': {
      const unitId = issue.cabinetId;
      const lineId = issue.ref;
      if (!unitId || !lineId) return [];
      return [
        {
          id: 'out-of-wall',
          label: 'Odmakni od zida',
          title: 'Pomjeri element tačno do unutrašnje strane zida.',
          apply: (d) => moveOutOfWall(d, evaluateProject(d), unitId, lineId),
        },
      ];
    }
    case 'panel.negative-position': {
      const inst = doc.assemblies.find((a) => a.id === issue.cabinetId);
      if (!inst || inst.transform.mirrored) return [];
      return [
        {
          id: 'origin',
          label: 'Pomjeri ishodište sklopa',
          title: 'Ploče se pomjeraju u pozitivno područje, a sklop ostaje na istom mjestu u prostoriji.',
          apply: (d) => normaliseAssemblyOrigin(d.assemblies.find((a) => a.id === inst.id)!),
        },
      ];
    }
    case 'edge.unresolved':
    case 'edge.override-orphaned':
      return edgeFixes(doc, issue);
    case 'link.orphaned':
    case 'link.out-of-sync':
      return linkFixes(doc, issue);
    default:
      return [];
  }
}
